/* eslint-disable @typescript-eslint/no-require-imports */
import { useCallback, memo, useMemo } from "react";
import { cn, copyToClipboard } from "@/lib/utils";

import "katex/dist/katex.min.css";
import MessageActionCard from "./MessageActionCard";
import { useArtifactStore } from "@/stores/artifact-store";
import { useMessageProcessor } from "@/hooks/useMessageProcessor";
import "./MessageBubbleCard.css";
import { filterStreamingArtifacts } from "@/lib/artifact-parser";
import { ArtifactButton } from "@/components/artifacts/ArtifactButton";
import { ParsedArtifactData } from "@/types/Artifact";
import FileAttachmentComponent, { FileAttachment } from "../../chat/FileAttachment";
import { MetaData } from "@shared/types/message";
import AgentProcessingCard from "./AgentProcessingCard";
import MastraErrorCard from "./MastraErrorCard";
import { useExtractTableFromLlmMessage } from "@/hooks/use-extract-table-from-llm-message";
import { useExtractMarkdownComponents } from "@/hooks/use-extract-markdown-components-from-llm-message";
import { useProcessElementsForChatRendering } from "@/hooks/use-process-elements-for-chat-rendering";
import { ContentBlock } from "@shared/types/content-block";
import { ContentBlockRenderer } from "./ContentBlockRenderer";
import { parseContentToBlocks, hasResolvedImageBlocks } from "@/hooks/use-parse-content-blocks";

type Props = {
    id: string;
    role: "user" | "assistant";
    content: string;
    threadId: string;
    isStreaming?: boolean;
    attachments?: FileAttachment[];
    metadata?: MetaData;
    onEdit?: (messageId: string, newContent: string) => void;
    /** Optional structured content blocks for assistant messages */
    contentBlocks?: ContentBlock[];
};

// Separate component for artifact buttons to prevent re-renders during streaming
const ArtifactButtonList = memo(
    ({
        artifacts,
        isStreaming,
        onArtifactClick,
    }: {
        artifacts: ParsedArtifactData[];
        messageId: string;
        isStreaming: boolean;
        onArtifactClick: (buttonId: string, type: string, title: string, isLoading: boolean) => void;
    }) => {
        if (artifacts.length === 0) return null;

        return (
            <div className="artifact-buttons-container mt-4 space-y-2">
                {artifacts.map((artifact) => {
                    const stableButtonId = `${artifact.type}_${artifact.title.replace(/\s+/g, "_")}`;
                    const isLoadingButton = isStreaming;

                    return (
                        <ArtifactButton
                            key={stableButtonId}
                            id={stableButtonId}
                            type={artifact.type}
                            title={artifact.title}
                            isLoading={isLoadingButton}
                            onClick={() =>
                                onArtifactClick(stableButtonId, artifact.type, artifact.title, isLoadingButton)
                            }
                        />
                    );
                })}
            </div>
        );
    },
    (prev, next) => {
        // Only re-render if artifacts actually change
        return (
            JSON.stringify(prev.artifacts.map((a) => ({ type: a.type, title: a.title }))) ===
                JSON.stringify(next.artifacts.map((a) => ({ type: a.type, title: a.title }))) &&
            prev.isStreaming === next.isStreaming
        );
    }
);

const MessageBubbleCard = ({
    id,
    role,
    content,
    isStreaming = false,
    threadId,
    attachments,
    metadata,
    onEdit,
    contentBlocks,
}: Props) => {
    const { openPanel, setActiveArtifact, getThreadArtifacts, restoreArtifact, getAllThreadItems } = useArtifactStore();
    // const { mutate: deleteMessage } = useDeleteMessageMutation();

    // Process message content and handle artifacts
    const { displayContent, artifacts, hasArtifacts } = useMessageProcessor({
        messageId: id,
        threadId,
        content,
        isStreaming,
        role,
    });

    // Handle artifact button clicks
    const handleArtifactClick = useCallback(
        (_: string, type: string, title: string, isLoading: boolean) => {
            // If it's a loading artifact, just open the panel to show the streaming content
            if (isLoading && isStreaming) {
                const allItems = getAllThreadItems(threadId);
                const loadingArtifact = allItems.find(
                    (item) => "isLoading" in item && item.isLoading && item.type === type && item.title === title
                );

                if (loadingArtifact) {
                    setActiveArtifact(loadingArtifact.id);
                    openPanel();
                }
                return;
            }

            // Regular artifact click handling
            const threadArtifacts = getThreadArtifacts(threadId);
            const targetArtifact = artifacts.find((a) => a.type === type && a.title === title);

            if (!targetArtifact) {
                console.warn("No artifact found for:", type, title);
                return;
            }

            const storeArtifact = threadArtifacts.find(
                (a) => a.type === targetArtifact.type && a.title === targetArtifact.title
            );

            if (storeArtifact) {
                restoreArtifact(storeArtifact.id);
                setActiveArtifact(storeArtifact.id);
                openPanel();
            } else {
                const allThreadArtifacts = Object.values(useArtifactStore.getState().artifacts).filter(
                    (a) => a.threadId === threadId
                );

                const hiddenArtifact = allThreadArtifacts.find(
                    (a) => a.type === targetArtifact.type && a.title === targetArtifact.title
                );

                if (hiddenArtifact) {
                    restoreArtifact(hiddenArtifact.id);
                    setActiveArtifact(hiddenArtifact.id);
                    openPanel();
                    return;
                }

                console.warn("Artifact not found in store:", targetArtifact);
            }
        },
        [
            artifacts,
            getAllThreadItems,
            getThreadArtifacts,
            isStreaming,
            openPanel,
            restoreArtifact,
            setActiveArtifact,
            threadId,
        ]
    );

    // Send all artifacts to panel
    const handleViewAllArtifacts = () => {
        if (hasArtifacts) {
            openPanel();
        }
    };

    // Extract tables from content
    const { textParts, tables } = useExtractTableFromLlmMessage(displayContent);

    // Markdown components without artifact button handling
    const markdownComponents = useExtractMarkdownComponents(role, threadId);

    const processedElements = useProcessElementsForChatRendering(textParts, tables, markdownComponents);

    // Parse content blocks from resolved image groups (for non-streaming assistant messages)
    const parsedContentBlocks = useMemo(() => {
        // If explicit contentBlocks provided, use those
        if (contentBlocks && contentBlocks.length > 0) {
            return contentBlocks;
        }
        // For assistant messages with resolved image blocks, parse them
        if (role === "assistant" && !isStreaming && hasResolvedImageBlocks(displayContent)) {
            return parseContentToBlocks(displayContent);
        }
        return null;
    }, [contentBlocks, role, isStreaming, displayContent]);

    const handleCopy = (content: string) => {
        const filteredContent = filterStreamingArtifacts(content).trim();
        copyToClipboard(filteredContent);
    };

    // Handle recreate functionality - only for assistant messages
    const handleRecreate = useCallback(() => {
        if (role !== "assistant") return;

        // Trigger regeneration by dispatching a custom event with the message ID
        // The Chat component will handle finding the user message and deleting this AI message
        window.dispatchEvent(
            new CustomEvent("recreateMessage", {
                detail: { messageId: id, threadId },
            })
        );
    }, [role, id, threadId]);

    return (
        <div id={`message-${id}`} className="group flex flex-col gap-0">
            {metadata?.agentProcessing && metadata.agentProcessing.steps?.length > 0 && (
                <AgentProcessingCard metadata={metadata} isStreaming={isStreaming} threadId={threadId} />
            )}
            {metadata?.agentProcessing?.error && role === "assistant" && (
                <MastraErrorCard
                    errorMessage={metadata.agentProcessing.error.message}
                    timestamp={metadata.agentProcessing.error.timestamp}
                    onRetry={handleRecreate}
                />
            )}
            <div
                className={cn(
                    "text-wrap break-words rounded-xl p-3",
                    role === "user"
                        ? "ml-auto max-w-[18rem] bg-primary/10 dark:bg-sidebar dark:text-gray-100 md:max-w-[16rem] lg:max-w-[20rem] 3xl:max-w-[38rem] 4xl:max-w-[32rem]"
                        : "assistant-message-bubble-card mr-auto w-full py-0 pb-1"
                )}
            >
                {/* Render attachments if present */}
                {attachments && attachments.length > 0 && (
                    <div className="mb-3 space-y-2">
                        {attachments.map((attachment) => (
                            <FileAttachmentComponent
                                key={attachment.id}
                                file={{
                                    id: attachment.id,
                                    url: (attachment as any).fileUrl || attachment.url,
                                    thumbnailUrl: attachment.thumbnailUrl,
                                    fileName: attachment.fileName,
                                    fileSize: attachment.fileSize || 0,
                                    mimeType:
                                        (attachment as any).fileType ||
                                        attachment.mimeType ||
                                        "application/octet-stream",
                                    analysis: attachment.analysis,
                                }}
                                compact={true}
                                showRemoveButton={false}
                            />
                        ))}
                    </div>
                )}

                {/* Render content: use ContentBlocks if available, otherwise use processedElements */}
                {parsedContentBlocks && parsedContentBlocks.length > 0 ? (
                    <ContentBlockRenderer contentBlocks={parsedContentBlocks} role={role} />
                ) : (
                    processedElements
                )}

                {/* Render artifact buttons separately */}
                {role === "assistant" && hasArtifacts && (
                    <ArtifactButtonList
                        artifacts={artifacts}
                        messageId={id}
                        isStreaming={isStreaming}
                        onArtifactClick={handleArtifactClick}
                    />
                )}
            </div>

            <div className="pb-2 pl-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <MessageActionCard
                    role={role}
                    messageId={id}
                    onCopy={() => handleCopy(content)}
                    onEdit={role === "user" && onEdit ? () => onEdit(id, content) : undefined}
                    onRecreate={role === "assistant" ? handleRecreate : undefined}
                    onViewInCanvas={role === "assistant" && hasArtifacts ? handleViewAllArtifacts : undefined}
                />
            </div>
        </div>
    );
};

export default MessageBubbleCard;
